<template>
  <div class="is-flex is-justify-content-center">
    <div class="contentView">
      <div class="buttons mb-1">
        <builderMenuBar @incrementWeek="incrementWeek" :showEditor="true" />
      </div>
      <!-- list of weeks -->
      <b-collapse
        class="card"
        animation="slide"
        v-for="(week, weekIdx) of selectedTemplate.weeks"
        :key="weekIdx"
        :open="isOpen == weekIdx"
        @open="isOpen = weekIdx"
      >
        <template #trigger="props">
          <div class="card-header" role="button">
            <p class="card-header-title mb-0">Week {{ weekIdx+1 }}</p>
            <a class="card-header-icon">
              <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"></b-icon>
            </a>
          </div>
        </template>
        <div class="card-content">
          <div class="content has-text-left">
            <div class="columns">
              <div class="column is-flex is-align-items-center">
                <b-button @click="incrementPlaylist(true)" class="is-info mr-2" rounded>
                  <strong>Add Playlist</strong>
                </b-button>
              </div>
              <div class="column is-flex is-justify-content-flex-end">
                <!-- only show the delete button on the last week -->
                <b-button
                  v-if="weekIdx === selectedTemplate.weeks.length-1 && weekIdx !== 0"
                  @click="openConfirm('deleteWeek', weekIdx)"
                  icon-right="delete"
                  class="is-danger"
                >
                  <strong>Remove Week</strong>
                </b-button>
              </div>
            </div>
            <!-- list of playlists in a week -->
            <div v-if="selectedTemplate.weeks[weekIdx]">
              <div v-for="(playlist, playlistIdx) in week.playlists" :key="playlistIdx">
                <div class="playlistContainer">
                  <div class="customColumns">
                    <div class="is-flex">
                      <div :style="`width: 295px; height: 166px;`" class="mr-3">
                        <!-- playlist length will never be 2. if you add one video, the count will be 3 due to the intro and outro videos -->
                        <img
                          v-if="playlist.videos.length >= 1"
                          :src="getScreenshots(playlist.videos)"
                          class="roundedImg"
                        />
                        <div
                          v-else
                          :style="`width: 295px; height: 166px;`"
                          class="roundedImg has-background-black"
                        />
                        <!-- width/height should match dimentions of the screenshot link used -->
                      </div>
                      <div
                        class="is-flex is-flex-direction-column"
                        :class="[ isViewingProgress ? 'is-justify-content-center' : 'is-justify-content-flex-start mt-4']"
                      >
                        <p class="title is-4 mb-1">{{ playlist.name }}</p>
                        <p class="mb-0">
                          <strong>{{ $root.convertTime(playlist.duration) }} | {{ exerciseCount(playlist.videos) }}</strong> Exercises <!-- // NEW minus 2 to account for intro/outro, since all playlists have them -->
                        </p>
                        <span v-if="isViewingProgress">
                          <p class="title is-4 has-text-info my-1">
                            {{ convertPercent(playlist.completion, playlist.duration) }}
                          </p>
                          <p>{{ getProgressDate(playlist.date) }}</p>
                        </span>
                      </div>
                    </div>
                    <div
                      class="is-flex is-flex-direction-column is-justify-content-space-between py-4 pr-4"
                    >
                      <div class="is-flex is-flex-direction-column is-align-items-flex-end">
                        <b-button
                          @click="selectPlaylist(playlist, playlistIdx)"
                          icon-right="pencil"
                          class="is-info mb-2"
                          rounded
                        >
                          <strong>Edit</strong>
                        </b-button>
                        <b-button
                          class="is-info mb-2"
                          outlined
                          rounded
                          icon-right="content-copy"
                          @click="openDuplicateMenu(playlist)"
                        >
                          <strong>Copy</strong>
                        </b-button>
                        <!-- show Send tells us if we're viewing an existing patients data or creating a new playlist -->
                        <b-button
                          @click="openConfirm('deletePlaylist', playlistIdx)"
                          type="is-text"
                          class="has-text-danger"
                          icon-right="delete"
                        >
                          <strong>Delete</strong>
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-collapse>
    </div>

    <!-- playlist editor -->
    <b-modal v-model="showEditPlaylist" :can-cancel="false">
      <editPlaylist :week="isOpen" :playlist="selectedPlaylist" />
    </b-modal>

    <!-- vimeo player -->
    <b-modal v-model="playVideo">
      <videoPlayer v-if="playVideo" :video="selectedVideo.vimeoID" />
    </b-modal>

    <!-- duplicate a playlist to the selected weeks -->
    <b-modal v-model="showDuplicateMenu" :width="335">
      <div v-if="showDuplicateMenu" class="card has-text-left">
        <div class="card-content">
          <div class="content">
            <p class="is-size-5 has-text-weight-thin mb-0">
              Copy:
              <span class="has-text-weight-bold">{{ playlistToDuplicate.name }}</span>
            </p>
            <!-- <p class="subtitle is-5">To:</p> -->
            <p class="is-size-5 has-text-weight-thin mb-2">To:</p>
            <b-field v-for="(item, idx) in selectedTemplate.weeks" :key="idx" class="ml-3">
              <b-tooltip :active="isCurrentWeek(item)" type="is-black" position="is-right">
                <template v-slot:content>
                  <p>
                    Create a duplicate
                    <br />playlist in the same week
                  </p>
                </template>
                <b-checkbox v-model="weeksToDuplicateTo" :native-value="item.week">
                  <p :class="{ 'has-text-weight-bold': isCurrentWeek(item) }">Week {{ item.week+1 }}</p>
                </b-checkbox>
              </b-tooltip>
            </b-field>
            <div class="buttons">
              <b-button @click="showDuplicateMenu = false;" class="is-info is-light" rounded>
                <strong>Cancel</strong>
              </b-button>
              <b-button @click="duplicatePlaylist()" class="is-info" icon-right="content-copy" rounded>
                <strong>Copy</strong>
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>


<script>
import { mapState } from "vuex";

import builderMenuBar from "@/components/builder/builderMenuBar";
import editPlaylist from "@/components/builder/editPlaylist";
import videoPlayer from "@/components/builder/videoPlayer";

export default {
  name: "Builder",
  props: {
    isViewingProgress: {
      // maybe need?
      default: false,
      type: Boolean
    }
  },
  components: {
    builderMenuBar,
    editPlaylist,
    videoPlayer
  },
  data() {
    return {
      isOpen: 0,
      playVideo: false,
      selectedVideo: null,
      selectedPlaylist: null,
      showEditPlaylist: false,
      showDuplicateMenu: false,
      playlistToDuplicate: null,
      weeksToDuplicateTo: [] //
    };
  },
  mounted() {
    if (!this.selectedTemplate) {
      // if the component is destroyed, we have no selectedTemplate when the component re renders
      this.$parent.close();
      // this.$store.commit('NEW_TEMPLATE')
    }
  },
  beforeDestroy() {
    this.$store.commit("SELECT_TEMPLATE", null);
  },
  computed: {
    // totalWeekDuration() { // not being used?
    //   let sum = 0;
    //   if (this.isOpen != null) {
    //     this.selectedTemplate.weeks[this.isOpen].playlists.forEach(playlist => {
    //       sum = sum + playlist.duration;
    //     });
    //   }
    //   return this.$root.convertTime(sum);
    // },
    ...mapState({
      user: state => state.auth.user,
      type: state => state.builderMenuType,
      selectedTemplate: state => state.selectedTemplate
    }),
  },
  methods: {
    isCurrentWeek(item) {
      // changes the styling in the duplication modal for the current week
      if (this.isOpen === item.week) {
        return true;
      } else return false;
    },
    openDuplicateMenu(playlist) {
      this.playlistToDuplicate = playlist;
      this.showDuplicateMenu = true;
    },
    duplicatePlaylist() {
      this.weeksToDuplicateTo.forEach(week => {
        // turning object to string prevents duplicates from syncing
        this.$store.commit(
          "COPY_PLAYLIST",
          JSON.stringify({ week, playlist: this.playlistToDuplicate })
        );
      });
      this.showDuplicateMenu = false;
      this.playlistToDuplicate = null;
      this.weeksToDuplicateTo = [];
      this.$buefy.toast.open("Success!");
    },
    convertPercent(number, duration) {
      const percent = Math.round((number / duration) * 100);
      return `${percent}% Completed`;
    },
    getProgressDate(date) {
      if (date) {
        return date;
      }
      return 'Not started.'
    },
    openConfirm(type, index) {
      let object = {
        type: "is-danger",
        hasIcon: true
      };
      switch (type) {
        case "deletePlaylist":
          object.title = "Delete Playlist";
          object.message = `Are you sure you want to <b>delete</b> the playlist?`;
          object.confirmText = "Delete Playlist";
          object.onConfirm = () => {
            this.incrementPlaylist(false, index);
            this.$buefy.toast.open("Playlist deleted!");
          };
          break;
        case "deleteWeek":
          object.title = "Delete Week";
          object.message = `Are you sure you want to <b>delete</b> week ${this
            .isOpen + 1}?`;
          object.confirmText = `Delete Week`;
          object.onConfirm = () => {
            this.isOpen = null;
            this.incrementWeek(false);
            this.$buefy.toast.open("Week deleted!");
          };
      }
      this.$buefy.dialog.confirm(object);
    },


    selectPlaylist(playlist, idx) {
      // console.log(playlist.videos)
      // avoid changing original, remove intro and outros before giving the ability to edit playlists
      let data = JSON.parse(JSON.stringify(playlist));
      // explicilty filter intro and outro from playlist editing
      data.videos = data.videos.filter(video => video.type === 'content');
      this.selectedPlaylist = { idx, data }; // used to pass to editPlaylist.vue
      this.showEditPlaylist = true;
    },
    exerciseCount(videos) { // NEW // count exercises in a video, without intros and outros
      const array = videos.filter(video => video.type !== 'content');
      return videos.length - array.length;
    },
    getScreenshots(videos) { // NEW
      if (videos.length < 1) {
        return 
      } else if (videos.length == 1) { // only ever one in the case of a playlist having a single video, which only happens before a playlist is sent to a patient, or is being saved to library
        return videos[0].screenshots[2].link
      } else if (videos.length >= 2) { // any other time, skip the first video just in case it is an intro.
        return videos[1].screenshots[2].link
      }
    },


    incrementPlaylist(value, playlist) {
      this.$store.commit("INCREMENT_PLAYLIST", {
        value,
        week: this.isOpen,
        playlist
      });
    },
    clearState() {
      this.$store.commit("NEW_TEMPLATE");
    },
    incrementWeek(add) {
      this.$store.commit("INCREMENT_WEEK", add);
    }
  }
};
</script>


<style lang="scss">
.width-100 {
  width: 100% !important;
}

.scrollableCol {
  // for the side by side playlist builder, in editPlaylist.vue and addToPlaylist.vue
  min-height: 70vh;
  max-height: 70vh;
  overflow: scroll;
}

.clippingBG {
  // holds the place of the screenshot images
  position: relative;
  width: 200px;
  height: 110px; /* 150px height, minus 20 from top, minus 20 from bottom. See clip Image */
  display: flex;
  align-items: center;
  justify-content: center;
}
.clipImage {
  position: absolute;
  clip-path: inset(20px 0 20px round 12px);
  border-radius: 12px;
}

// styling for video thumbnails
.iconContainer {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(black, 0.2);
  border-radius: 12px;
}
.timeContainer {
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  padding: 0 .75rem;
}

.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
// .list-group-item {
//   cursor: move;
// }
.list-group-item li {
  cursor: pointer;
}

.playlistContainer {
  background-color: hsl(0, 0%, 93.5%);
  // background-color: #F5F7F9;
  border-radius: 12px;
  margin-bottom: 2vh;
  box-shadow: 2px 2px 4px #c1c1c1;
  border: solid 1px hsl(0, 0%, 86%);
  transition: box-shadow .3s ease-in-out;
  &:hover {
    box-shadow: 2px 4px 8px #c1c1c1;
  }
}
.customColumns {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  max-height: 166px!important;
}
.roundedImg {
  border-radius: 12px 0 0 12px;
}
</style>